/*
 * AddressForm.tsx : Un formulaire pour ajouter ou modifier une adresse.
 */

// Import React
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Import FontAwesome
import { faMapLocationDot } from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	CheckboxField,
	finishFormSave,
	FormSaveActions,
	InputField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';

// Import Adresse
import { AddressData } from '../types/types';
import { addressesApi } from '../services/addressesApi';

// Composant AddressForm
const AddressForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<AddressData>({
		id: 0,
		label: '',
		types: [],
		line1: '',
		zipCode: '',
		city: '',
		country: '',
		status: true,
		createdAt: new Date(),
		createdBy: {
			id: 0,
			name: '',
			firstName: '',
		},
		tiers: { id: 0, label: '' },
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const tiersIdParam = searchParams.get('tiersId') || '';
	const tiersLabelParam = searchParams.get('tiersLabel') || '';
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch de l'adresse et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			addressesApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on préremplit le tiers si présent dans les paramètres
			if (tiersIdParam) {
				const tiersId = Number(tiersIdParam) || 0;
				const tiersLabel = tiersLabelParam || '';
				setFormData({
					...formData,
					tiers: { id: tiersId, label: tiersLabel },
				});
			}
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () =>
			formData.tiers?.id
				? navigate(`/app/tiers/${formData.tiers.id}/manage`)
				: navigate('/app/addresses');
		// Si un id est présent, mise à jour de l'adresse
		if (id) {
			addressesApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
			// Sinon, création d'une nouvelle adresse
		} else {
			addressesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/addresses/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faMapLocationDot}
					title={id ? "Édition d'une adresse" : 'Nouvelle adresse'}
					isActive={formData.status}
					onToggle={(value) => setFormData({ ...formData, status: value })}
					createdAt={
						formData.createdAt ? formData.createdAt.toLocaleString() : undefined
					}
					createdBy={
						formData.createdBy?.name
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
								/>
								{/* Input pour l'ID du tiers associé. Il n'y en a qu'un seul dans tiers */}
								<InputField
									label="Tiers associé (ID)"
									name="tiersId"
									value={
										formData.tiers
											? String(formData.tiers.id)
											: 'tiers non associé'
									}
									onChange={(e) => {
										const id = Number(e.target.value) || 0;
										const newTiers =
											formData.tiers && formData.tiers.id
												? { ...formData.tiers }
												: { id, label: '' };
										setFormData({ ...formData, tiers: newTiers });
									}}
									readOnly={true}
									visibility={false}
									loading={loading}
								/>
								{/* Input pour le label du tiers associé. Il n'y en a qu'un seul dans tiers. N'est pas modifiable */}
								<InputField
									label="Label du tiers associé"
									name="tiersLabel"
									value={formData.tiers ? formData.tiers.label : ''}
									onChange={(e) => {
										const label = e.target.value;
										const newTiers = formData.tiers
											? { ...formData.tiers, label }
											: { id: 0, label };
										setFormData({ ...formData, tiers: newTiers });
									}}
									readOnly={true}
									loading={loading}
								/>
								{/* Input masqué pour les types d'adresse */}
								<InputField
									label="Types d'adresse"
									name="types"
									value={formData.types.map((type) => type.code).join(', ')}
									onChange={(e) =>
										setFormData({
											...formData,
											types: e.target.value
												.split(',')
												.map((value) => ({ code: value.trim(), label: '' })),
										})
									}
									visibility={false}
									loading={loading}
								/>
								{/* Les checkboxes suivants modifient l'input "types" */}
								<CheckboxField
									label="Principale"
									checked={formData.types.some((type) => type.code === 'PRI')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'PRI'
										);
										if (value) {
											newTypes.push({ code: 'PRI', label: 'Principale' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Devis"
									checked={formData.types.some((type) => type.code === 'DEV')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'DEV'
										);
										if (value) {
											newTypes.push({ code: 'DEV', label: 'Devis' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Commande"
									checked={formData.types.some((type) => type.code === 'COM')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'COM'
										);
										if (value) {
											newTypes.push({ code: 'COM', label: 'Commande' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Facturation"
									checked={formData.types.some((type) => type.code === 'FAC')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'FAC'
										);
										if (value) {
											newTypes.push({ code: 'FAC', label: 'Facturation' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Livraison"
									checked={formData.types.some((type) => type.code === 'LIV')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'LIV'
										);
										if (value) {
											newTypes.push({ code: 'LIV', label: 'Livraison' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Autre"
									checked={formData.types.some((type) => type.code === 'AUT')}
									onChange={(value) => {
										const newTypes = formData.types.filter(
											(type) => type.code !== 'AUT'
										);
										if (value) {
											newTypes.push({ code: 'AUT', label: 'Autre' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Adresse Ligne 1"
									name="line1"
									value={formData.line1}
									onChange={handleChange}
									required
									loading={loading}
								/>
								<InputField
									label="Adresse Ligne 2"
									name="line2"
									value={formData.line2}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Code Postal"
									name="zipCode"
									value={formData.zipCode}
									onChange={handleChange}
									required
									loading={loading}
								/>
								<InputField
									label="Ville"
									name="city"
									value={formData.city}
									onChange={handleChange}
									required
									loading={loading}
								/>
								<InputField
									label="Pays"
									name="country"
									value={formData.country}
									onChange={handleChange}
									required
									loading={loading}
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() =>
							navigate(
								formData.tiers?.id
									? `/app/tiers/${formData.tiers?.id}/manage`
									: '/app/addresses'
							)
						}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default AddressForm;
